// BOORO: the detail of one agent. A panel with the settings first, then the
// work of the agent and the knowledge it reads; the chat opens from the head.
import { Trans, useLingui } from "@lingui/react/macro";
import type { Bot, TaskThreadEntry, TeamAgent } from "@rakazo/contracts";
import { Button, Input, Label, Switch, Tabs, TabsList, TabsTrigger } from "@rakazo/ui-web";
import { MessageSquare, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { rpc } from "../../lib/rpc";
import { useTaskDisplayStatusLabels } from "../../lib/task-display-status";
import { errorText } from "../../lib/user-error";
import { BotSettings } from "./bot-panel";
import { ClearConversationDialog } from "./dialogs";

type Tab = "settings" | "work" | "knowledge";

export function AgentDetailPanel({
  botId,
  onClose,
  onChanged,
}: {
  botId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("settings");
  const [bot, setBot] = useState<Bot | null>(null);
  const [error, setError] = useState("");

  const loadBot = useCallback(async () => {
    try {
      const next = await rpc.bots.get({ botId });
      setBot(next);
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    }
  }, [botId]);

  useEffect(() => {
    void loadBot();
  }, [loadBot]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <aside
      data-testid="agent-detail-panel"
      aria-label={bot?.name ?? t`Agent`}
      className="fixed inset-y-0 end-0 z-50 flex w-full max-w-xl flex-col border-s border-border bg-background shadow-xl"
    >
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[15px] font-medium text-foreground" dir="auto">
            {bot?.name ?? t`Agent`}
          </h2>
          {bot?.title ? (
            <p className="truncate text-[12.5px] text-muted-foreground" dir="auto">
              {bot.title}
            </p>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t`Open in chat`}
          onClick={() => navigate(`/app/${botId}`)}
        >
          <MessageSquare />
        </Button>
        <Button variant="ghost" size="icon-sm" aria-label={t`Close`} onClick={onClose}>
          <X />
        </Button>
      </div>

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as Tab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="border-b border-border px-4 py-2">
          <TabsList aria-label={t`Details`}>
            <TabsTrigger value="settings">
              <Trans>Settings</Trans>
            </TabsTrigger>
            <TabsTrigger value="work">
              <Trans>Tasks</Trans>
            </TabsTrigger>
            <TabsTrigger value="knowledge">
              <Trans>Knowledge</Trans>
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="rk-scroll flex-1 overflow-y-auto px-4 py-4">
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          {tab === "settings" && bot ? <AgentSettings bot={bot} onChanged={onChanged} /> : null}
          {tab === "work" ? (
            <div className="space-y-6">
              <AgentTasks botId={botId} />
              <AgentRoutines botId={botId} />
            </div>
          ) : null}
          {tab === "knowledge" ? <AgentHandbook botId={botId} /> : null}
        </div>
      </Tabs>
    </aside>
  );
}

/** The tasks of the agent, with their state. */
function AgentTasks({ botId }: { botId: string }) {
  const { t } = useLingui();
  const statuses = useTaskDisplayStatusLabels();
  const [tasks, setTasks] = useState<TaskThreadEntry[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    void rpc.booroTasks
      .forBot({ botId })
      .then((next) => {
        if (active) setTasks(next);
      })
      .catch((cause) => {
        if (active) setError(errorText(cause));
      });
    return () => {
      active = false;
    };
  }, [botId]);

  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    );
  }
  if (tasks.length === 0) {
    return <p className="text-sm text-muted-foreground">{t`No tasks yet`}</p>;
  }
  return (
    <ul className="space-y-1.5">
      {tasks.map((task) => (
        <li
          key={task.ticketId}
          data-testid={`agent-task-${task.ticketId}`}
          className="rounded-xl border border-border px-3 py-2"
        >
          <div className="flex items-center justify-between gap-3">
            <span className="truncate text-[13.5px] text-foreground" dir="auto">
              {task.title}
            </span>
            <span className="shrink-0 text-[12px] text-muted-foreground">
              {statuses[task.displayStatus]}
            </span>
          </div>
          {task.result ? (
            <p className="mt-0.5 truncate text-[12.5px] text-muted-foreground" dir="auto">
              {task.result}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** The routines of the agent. */
function AgentRoutines({ botId }: { botId: string }) {
  const { t } = useLingui();
  const [routines, setRoutines] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    void rpc.routines
      .list({ botId })
      .then((next) => {
        if (!active) return;
        setRoutines(next.map((routine) => ({ id: routine.id, name: routine.name })));
      })
      .catch((cause) => {
        if (active) setError(errorText(cause));
      });
    return () => {
      active = false;
    };
  }, [botId]);

  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    );
  }
  if (routines.length === 0) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-[13px] text-muted-foreground">{t`Routines`}</h3>
      <ul className="space-y-1.5">
        {routines.map((routine) => (
          <li
            key={routine.id}
            className="rounded-xl border border-border px-3 py-2 text-[13.5px] text-foreground"
            dir="auto"
          >
            {routine.name}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The pages of the knowledge this agent may read. Only title and path: the
 * content belongs to the person who wrote it.
 */
function AgentHandbook({ botId }: { botId: string }) {
  const { t } = useLingui();
  const [pages, setPages] = useState<{ id: string; title: string; path: string }[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    void rpc.booroKnowledge
      .forAgent({ botId })
      .then((next) => {
        if (active) setPages(next);
      })
      .catch((cause) => {
        if (active) setError(errorText(cause));
      });
    return () => {
      active = false;
    };
  }, [botId]);

  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    );
  }
  if (pages.length === 0) {
    return <p className="text-sm text-muted-foreground">{t`No pages yet`}</p>;
  }
  return (
    <ul data-testid="agent-handbook" className="space-y-1.5">
      {pages.map((page) => (
        <li key={page.id} className="rounded-xl border border-border px-3 py-2">
          <p className="truncate text-[13.5px] text-foreground" dir="auto">
            {page.title}
          </p>
          <p className="truncate text-[12px] text-muted-foreground">{page.path}</p>
        </li>
      ))}
    </ul>
  );
}

/**
 * The computer of the agent. The computer of a space is shared; an agent that
 * works with confidential data or needs the screen to itself gets one of its
 * own – the person decides, never the system on its own.
 */
function AgentComputer({ botId, onChanged }: { botId: string; onChanged: () => void }) {
  const { t } = useLingui();
  const [agent, setAgent] = useState<TeamAgent | null>(null);
  const [needs, setNeeds] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const agents = await rpc.booroTeam.agents();
      const found = agents.find((entry) => entry.id === botId) ?? null;
      setAgent(found);
      setNeeds(found?.needsOwnComputer ?? false);
      setReason(found?.computerReason ?? "");
    } catch (cause) {
      setError(errorText(cause));
    }
  }, [botId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(nextNeeds: boolean) {
    setBusy(true);
    setError("");
    try {
      await rpc.booroTeam.setComputerNeed({
        botId,
        needsOwnComputer: nextNeeds,
        reason: nextNeeds ? reason : undefined,
      });
      if (nextNeeds) {
        // The agent gets its own computer now.
        await rpc.bots.setComputer({ botId, mode: "dedicated" });
      } else {
        await rpc.bots.setComputer({ botId, mode: "team" });
      }
      setNeeds(nextNeeds);
      await load();
      onChanged();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  // The Chief manages the account; it works without any computer.
  if (agent?.isChief) {
    return (
      <div data-testid="agent-computer" className="mt-4">
        <div className="text-[14px] text-muted-foreground">
          <Trans>Computer</Trans>
        </div>
        <p className="mt-1 text-[13.5px] text-foreground/80">
          <Trans>The Chief manages the account and works without a computer.</Trans>
        </p>
      </div>
    );
  }

  return (
    <div data-testid="agent-computer" className="mt-4 space-y-2">
      <div className="text-[14px] text-muted-foreground">
        <Trans>Computer</Trans>
      </div>
      <p className="text-[13.5px] text-foreground/80">
        {agent?.hasOwnComputer ? t`Own computer` : t`Team computer`}
      </p>
      {agent?.sharedWith.length ? (
        <p className="text-[12.5px] text-muted-foreground">
          <Trans>Shared with</Trans> {agent.sharedWith.join(", ")}
        </p>
      ) : null}

      <div className="space-y-2 rounded-xl border border-border px-3 py-3">
        <div className="flex items-center justify-between gap-3">
          <Label className="text-[13.5px] font-normal text-foreground">
            <Trans>Needs its own computer</Trans>
          </Label>
          <Switch
            checked={needs}
            disabled={busy}
            aria-label={t`Needs its own computer`}
            onCheckedChange={(on) => void save(on)}
          />
        </div>
        {needs ? (
          <div className="space-y-1">
            <Label className="text-[12.5px] text-muted-foreground" htmlFor={`${botId}-reason`}>
              <Trans>Reason</Trans>
            </Label>
            <Input
              id={`${botId}-reason`}
              value={reason}
              disabled={busy}
              placeholder={t`Confidential data`}
              onChange={(event) => setReason(event.target.value)}
              onBlur={() => void save(true)}
            />
          </div>
        ) : null}
      </div>

      {error ? (
        <p role="alert" className="text-[13px] text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** The settings of the agent, with the conversation tools of the chat. */ function AgentSettings({
  bot,
  onChanged,
}: {
  bot: Bot;
  onChanged: () => void;
}) {
  const [clearing, setClearing] = useState(false);
  return (
    <>
      <BotSettings
        key={bot.id}
        bot={bot}
        memoryProviderConfigured={false}
        onSkillsChange={() => undefined}
        onSave={async ({ computerMode, ...patch }) => {
          if (computerMode !== undefined && computerMode !== bot.computerMode) {
            await rpc.bots.setComputer({ botId: bot.id, mode: computerMode });
          }
          await rpc.bots.update({ botId: bot.id, ...patch });
          onChanged();
        }}
        onExport={async () => {
          const manifest = await rpc.export.bot({ botId: bot.id });
          const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${bot.name.toLowerCase().replace(/\s+/g, "-")}-export.json`;
          link.click();
          URL.revokeObjectURL(url);
        }}
        onClear={() => setClearing(true)}
        computerSection={<AgentComputer botId={bot.id} onChanged={onChanged} />}
      />
      {clearing ? (
        <ClearConversationDialog
          bot={bot}
          onCancel={() => setClearing(false)}
          onConfirm={async () => {
            await rpc.threads.clear({ botId: bot.id });
            setClearing(false);
          }}
        />
      ) : null}
    </>
  );
}
